import { useMutation } from "@tanstack/react-query";
import { markFeedbackReviewed, type ApiError } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

/**
 * "Mark done" mutation for the `/feedback` surfaces.
 *
 * Marks a note, playlist, or clip done via
 * `POST /api/my-feedback/review`. Exactly one of `note_id`,
 * `playlist_id`, or `clip_id` must be set per call (router
 * returns 422 otherwise). On success it invalidates the
 * `["my-feedback"]` bundle so the Reviewed/New pills refresh.
 *
 * The backend re-validates visibility at mark-time, so a guessed
 * id 403s rather than recording a review — the client doesn't
 * need to pre-check.
 *
 * PR 7 added `clip_id` support; pre-PR 7 callers that only pass
 * `note_id` / `playlist_id` keep working unchanged.
 */
export function useFeedbackReview() {
  const mutation = useMutation({
    mutationFn: (body: {
      note_id?: number;
      playlist_id?: number;
      /** PR 7: clip review target. */
      clip_id?: number;
      /** PR 6: structured player response signal encoded via
       * `responseSignals.encodeReflection`. Backwards-compatible —
       * if absent, the existing "mark done without text"
       * behavior is preserved. */
      reflection?: string;
    }) => markFeedbackReviewed(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-feedback"] });
    },
  });
  return {
    markReviewed: (body: {
      note_id?: number;
      playlist_id?: number;
      clip_id?: number;
      reflection?: string;
    }) => mutation.mutateAsync(body),
    isPending: mutation.isPending,
    error: (mutation.error as ApiError | null) ?? null,
  };
}
